import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconX, IconPlus, IconMinus, IconCurrencyTaka, IconChevronDown, IconFileInvoice } from '@tabler/icons-react';
import { useIncomeExpenseStore } from '../../store/incomeExpenseStore';
import { useAuthStore } from '../../store/authStore';
import incomeExpenseService from '../../services/incomeExpenseService';
import { notifications } from '@/lib/notifications';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { buildSuggestionsForType, defaultActiveSuggestionForType } from '../../lib/incomeExpenseDefaults';
import { useOfficeSpaceStore } from '../../store/officeSpaceStore';
import DatePickerField from './DatePickerField';
import CustomerPicker from './CustomerPicker';

const BN_DIGITS = '০১২৩৪৫৬৭৮৯';
const EN_DIGITS = '0123456789';

function toEnglishDigits(str) {
    return str.replace(/[০-৯]/g, (ch) => EN_DIGITS[BN_DIGITS.indexOf(ch)]);
}
function toBanglaDigits(str) {
    return String(str).replace(/[0-9]/g, (ch) => BN_DIGITS[parseInt(ch, 10)]);
}

const QUICK_AMOUNTS = [20, 30, 50, 60, 100];
// দেনা পাওনা deals with larger owed sums → bigger chips + coarser wheel step.
const DENA_QUICK_AMOUNTS = [1000, 2000, 5000, 10000];
const SCROLL_STEP = 5;
const DENA_SCROLL_STEP = 100;

// Today's date in the user's local timezone as YYYY-MM-DD (for the date input).
function localToday() {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
}

// Current local wall-clock time as "HH:mm" (default for the back-dated time picker).
function localNowTime() {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// Combine a picked YYYY-MM-DD + HH:mm into a full ISO instant (local wall-clock →
// UTC), so a back-dated entry lands on the exact chosen day AND time instead of
// midnight. The backend stores this straight into createdAt.
function ymdTimeToIso(ymd, hm) {
    if (!ymd) return '';
    const [y, m, d] = ymd.split('-').map(Number);
    const [hh, mm] = (hm || '00:00').split(':').map(Number);
    const dt = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
    return isNaN(dt.getTime()) ? '' : dt.toISOString();
}

// Bridge the YYYY-MM-DD string state to/from the Date object DatePickerField uses.
function ymdToDate(s) {
    return s ? new Date(`${s}T00:00:00`) : null;
}
function dateToYmd(d) {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export default function IncomeExpenseModal() {
    const { t, currentLanguage } = useTranslation();
    const { isOpen, prefill, closeModal, openModal } = useIncomeExpenseStore();
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const isBangla = currentLanguage === 'bn';
    // When the prefill carries an entry id, the modal edits that entry (update)
    // instead of creating a new one.
    const editId = prefill?.id ?? null;
    const isEditing = editId != null;
    // দেনা পাওনা mode (opened from that tab / Ctrl+Shift+I): the SAME ledger entry,
    // but always বাকি — income tab reads পাওনা, expense reads দেনা, and the
    // নগদ/বাকি toggle is hidden (there is nothing to choose).
    const denaPawna = !!prefill?.denaPawna;

    // Global shortcut: Ctrl/⌘+Shift+I opens the দেনা পাওনা add modal from anywhere.
    useEffect(() => {
        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
                e.preventDefault();
                openModal({ denaPawna: true });
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [openModal]);

    const [name, setName]               = useState('');
    const [type, setType]               = useState('income');
    const [amountDisplay, setAmountDisplay] = useState('');
    const [note, setNote]               = useState('');
    const [date, setDate]               = useState(localToday);
    // Wall-clock time for the entry — only editable when the entry is back-dated
    // (today keeps the real server timestamp). Defaults to the current time.
    const [time, setTime]               = useState(localNowTime);
    const [isDue, setIsDue]             = useState(false);
    const [saving, setSaving]           = useState(false);
    const [amountError, setAmountError] = useState('');
    const [nameError, setNameError]     = useState('');
    const [nameOpen, setNameOpen]       = useState(false); // name dropdown visibility
    // Optional customer link — feeds `customerId` on the entry and pre-fills a
    // generated invoice. Name is free text; picking from the list sets the id.
    const [customerId, setCustomerId]           = useState(null);
    const [customerName, setCustomerName]       = useState('');
    const [customerPhone, setCustomerPhone]     = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    // Today's running income/expense totals — shown as floating badges on the type
    // toggle so the shopkeeper sees the day's tally while adding an entry. Refetched
    // each time the modal opens so it reflects entries saved since the last open.
    const [todayTotals, setTodayTotals] = useState(null);
    const amountRef = useRef(null);
    const nameBoxRef = useRef(null);
    const isIncome = type === 'income';

    // Close the name dropdown on any click outside its wrapper.
    useEffect(() => {
        const handler = (e) => { if (nameBoxRef.current && !nameBoxRef.current.contains(e.target)) setNameOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Esc closes the open name dropdown first, otherwise the whole modal.
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (nameOpen) setNameOpen(false);
            else closeModal();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, nameOpen, closeModal]);

    // Suggestions are TYPE-AWARE and carry a price: income and expense each have their
    // own module/predefined/custom list ({name, price, category}), price-first deduped.
    const nameSuggestions = buildSuggestionsForType(user?.settings, type);
    const norm = (s) => (s || '').normalize('NFC').trim();
    // Look up a suggestion object by its display name (normalized).
    const findSuggestionByName = (list, nm) => list.find((s) => norm(s.name) === norm(nm)) || null;

    // Tracks whether the user has set the amount themselves (typed / wheel / quick chip).
    // While false, clicking a priced name badge auto-fills its price; once true we never
    // clobber the user's amount.
    const amountTouchedRef = useRef(false);

    // The suggestion NAME pre-selected for a given type (normalized match).
    // Expense opens with an EMPTY name — the shopkeeper types/picks each expense, so we
    // never pre-select a default there (income still pre-selects its active module).
    const pickDefaultName = (forType) => {
        if (forType === 'expense') return '';
        const list = buildSuggestionsForType(user?.settings, forType);
        const active = defaultActiveSuggestionForType(forType);
        return list.find((s) => norm(s.name) === norm(active))?.name || '';
    };

    // Switching income ↔ expense swaps the suggestion set, so re-select that type's
    // default name — unless the current name is a manual/prefilled value the user typed.
    const switchType = (nextType) => {
        if (nextType === type) return;
        const inCurrentList = nameSuggestions.some((s) => norm(s.name) === norm(name));
        if (!name.trim() || inCurrentList) setName(pickDefaultName(nextType));
        setType(nextType);
        setNameError('');
    };

    useEffect(() => {
        if (isOpen) {
            // Default-select the type's active suggestion (e.g. "ফটো এডিট" for income)
            // when nothing is prefilled. Match normalized + select the LIST's exact string
            // so the badge (name === s) highlights even if the stored value differs by
            // Bengali Unicode normalization/whitespace.
            const nextType = prefill?.type || 'income';
            // দেনা পাওনা: the user types the name manually (no module pre-select).
            const defaultName = prefill?.denaPawna ? '' : pickDefaultName(nextType);
            setName(prefill?.name || defaultName);
            setType(nextType);
            const numVal = prefill?.amount != null ? String(prefill.amount) : '';
            setAmountDisplay(isBangla && numVal ? toBanglaDigits(numVal) : numVal);
            // Editing keeps the entry's exact amount (no priced-name auto-fill clobber).
            amountTouchedRef.current = isEditing;
            const noteByCategory = {
                image_generation: t('incomeExpense.categoryPostfixImage'),
                doc_generation:   t('incomeExpense.categoryPostfixDoc'),
                ai_template:      t('incomeExpense.categoryPostfixAiTemplate'),
                print_media:      t('incomeExpense.categoryPostfixPrintMedia'),
                manual_edit:      t('incomeExpense.categoryPostfixManualEdit'),
            };
            // Edit: use the entry's own note + date. Create: category default note, today.
            setNote(isEditing ? (prefill?.note || '') : (noteByCategory[prefill?.category] || ''));
            setDate(isEditing && prefill?.date ? prefill.date : localToday());
            // Edit: use the entry's own time (so re-saving doesn't shift it); create: now.
            setTime(isEditing && prefill?.time ? prefill.time : localNowTime());
            setIsDue(prefill?.denaPawna ? true : (prefill?.isDue || false));
            setCustomerId(prefill?.customerId ?? null);
            setCustomerName(prefill?.customerName || '');
            setCustomerPhone(prefill?.customerPhone || '');
            setCustomerAddress(prefill?.customerAddress || '');
            setAmountError('');
            setNameError('');
            setTimeout(() => amountRef.current?.focus(), 50);
        }
    }, [isOpen, prefill]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load today's running totals whenever the modal opens (for the toggle badges).
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        const today = localToday();
        incomeExpenseService.summary({ dateFrom: today, dateTo: today })
            .then((res) => { if (!cancelled) setTodayTotals(res?.data ?? null); })
            .catch(() => { if (!cancelled) setTodayTotals(null); });
        return () => { cancelled = true; };
    }, [isOpen]);

    const handleAmountChange = (e) => {
        const cleaned = e.target.value.replace(/[^0-9০-৯.]/g, '');
        setAmountDisplay(cleaned);
        amountTouchedRef.current = true;
        if (cleaned) setAmountError('');
    };

    // Mouse-wheel on amount field: step by SCROLL_STEP (multiples of 5)
    // Must use a non-passive native listener so preventDefault() actually works.
    const amountDisplayRef = useRef(amountDisplay);
    useEffect(() => { amountDisplayRef.current = amountDisplay; }, [amountDisplay]);

    useEffect(() => {
        const el = amountRef.current;
        if (!el) return;
        const step = denaPawna ? DENA_SCROLL_STEP : SCROLL_STEP;
        const onWheel = (e) => {
            e.preventDefault();
            const current = parseFloat(toEnglishDigits(amountDisplayRef.current)) || 0;
            const delta = e.deltaY < 0 ? step : -step;
            const next = Math.max(0, Math.round((current + delta) / step) * step);
            setAmountDisplay(isBangla ? toBanglaDigits(String(next)) : String(next));
            amountTouchedRef.current = true;
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [isBangla, isOpen, denaPawna]);

    const setQuickAmount = (val) => {
        setAmountDisplay(isBangla ? toBanglaDigits(String(val)) : String(val));
        amountTouchedRef.current = true;
        amountRef.current?.focus();
    };

    // −10 / +10 stepper buttons beside the amount field (never below 0).
    const stepAmount = (delta) => {
        const current = parseFloat(toEnglishDigits(amountDisplay)) || 0;
        const next = Math.max(0, current + delta);
        setAmountDisplay(isBangla ? toBanglaDigits(String(next)) : String(next));
        amountTouchedRef.current = true;
        setAmountError('');
        amountRef.current?.focus();
    };

    // Picking a name from the dropdown: set it and, when it's a PRICED service and the
    // user hasn't set the amount yet, auto-fill that price. Priceless names never touch it.
    const pickName = (s) => {
        setName(s.name);
        setNameError('');
        setNameOpen(false);
        if (s.price != null && !amountTouchedRef.current) {
            setAmountDisplay(isBangla ? toBanglaDigits(String(s.price)) : String(s.price));
            setAmountError('');
        }
    };

    const getEnglishAmount = () => parseFloat(toEnglishDigits(amountDisplay));

    // Inline validation — surfaced below the relevant field instead of a toast.
    // Returns the numeric amount on success, or null (with errors shown) on failure.
    const validate = () => {
        const numAmount = getEnglishAmount();
        const amtInvalid = !amountDisplay || isNaN(numAmount);
        const nameInvalid = !name.trim();
        setAmountError(amtInvalid ? t('incomeExpense.amountRequired') : '');
        setNameError(nameInvalid ? t('incomeExpense.nameRequired') : '');
        if (amtInvalid) { amountRef.current?.focus(); return null; }
        if (nameInvalid) return null;
        return numAmount;
    };

    // The shop this entry belongs to. An operator's entry then lands in the OWNER's ledger,
    // tagged with the operator (the backend resolves the ledger owner from this id).
    const activeOfficeSpaceId = useOfficeSpaceStore((s) => s.selectedOfficeSpace?.id);

    const buildPayload = (numAmount) => ({
        name: name.trim(),
        type,
        amount: numAmount,
        category: prefill?.category || 'manual',
        // Only send referenceId when it's a real positive integer. Some sources pass a
        // non-numeric id (e.g. the string 'current' for a just-generated image, or a UUID),
        // which the backend rejects with "reference id is not integer" — coerce those to null.
        referenceId: (() => {
            const n = Number(prefill?.referenceId);
            return Number.isInteger(n) && n > 0 ? n : null;
        })(),
        note: note.trim() || null,
        isDue,
        ...(activeOfficeSpaceId ? { officeSpaceId: activeOfficeSpaceId } : {}),
        // Optional link to a saved customer. Only sent when one is actually linked, so
        // walk-in entries keep the exact legacy payload (safe before the backend column
        // ships; no unknown-field rejection risk).
        ...(customerId ? { customerId } : {}),
        // Only send a date when the user back-dates the entry; today keeps the
        // real server timestamp so same-day entries stay in creation order. A
        // back-dated entry carries the picked day AND time.
        ...(date && date !== localToday() ? { date: ymdTimeToIso(date, time) } : {}),
    });

    // Update payload for edit mode — matches the backend PUT fields (customerId sent
    // always so a customer can be linked/unlinked; date always sent to keep the picked day).
    const buildUpdatePayload = (numAmount) => ({
        name: name.trim(),
        type,
        amount: numAmount,
        isDue,
        note: note.trim() || null,
        customerId: customerId || null,
        ...(date ? { date: ymdTimeToIso(date, time) } : {}),
    });

    const handleSave = async () => {
        const numAmount = validate();
        if (numAmount == null) return;
        setSaving(true);
        try {
            if (isEditing) {
                await incomeExpenseService.update(editId, buildUpdatePayload(numAmount));
                notifications.show({ message: t('incomeExpense.updated') || t('incomeExpense.saved'), color: 'green' });
            } else {
                await incomeExpenseService.create(buildPayload(numAmount));
                notifications.show({ message: t('incomeExpense.saved'), color: 'green' });
            }
            closeModal();
        } catch (err) {
            notifications.show({ message: err.message || t('common.error'), color: 'red' });
        } finally {
            setSaving(false);
        }
    };

    // Save the entry (once), then jump to the invoice form pre-filled with this
    // customer + service + amount. `fromLedger` tells the invoice page the income is
    // already recorded, so it disables its own "add to ledger" (no double-counting).
    const handleSaveAndInvoice = async () => {
        const numAmount = validate();
        if (numAmount == null) return;
        setSaving(true);
        try {
            await incomeExpenseService.create(buildPayload(numAmount));
        } catch (err) {
            notifications.show({ message: err.message || t('common.error'), color: 'red' });
            setSaving(false);
            return;
        }
        setSaving(false);
        notifications.show({ message: t('incomeExpense.saved'), color: 'green' });
        const draft = {
            customer: { id: customerId || null, name: customerName.trim(), phone: customerPhone || '', address: customerAddress || '' },
            items: [{ name: name.trim(), qty: 1, price: numAmount }],
            status: isDue ? 'due' : 'paid',
            fromLedger: true,
        };
        closeModal();
        navigate('/income?tab=invoice', { state: { invoiceDraft: draft } });
    };

    if (!isOpen) return null;

    const accentActive = isIncome ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white';
    const ringColor    = isIncome ? 'focus:ring-emerald-400/40' : 'focus:ring-red-400/40';
    const saveBg       = isIncome
        ? 'bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-500/50'
        : 'bg-red-500 hover:bg-red-600 disabled:bg-red-500/50';

    // Name is chosen from the saved prefixes as badges. A prefilled name (e.g. from
    // image/doc generation) is surfaced as a badge too so it stays selectable.
    // Badge list as {name, price, category} objects. A prefilled name not already in the
    // list is prepended (carrying the prefill amount as its price so the badge shows it).
    // INCOME lists ONLY services that have a price set (modules always qualify; picked
    // shop services appear once the owner prices them in Settings). EXPENSE lists EVERY
    // saved name whether priced or not — a shop logs expenses like "কাগজ" without a fixed
    // unit price. Any name can still be typed manually. A prefilled name is surfaced
    // regardless so it stays selectable.
    const baseSuggestions = isIncome ? nameSuggestions.filter((s) => s.price != null) : nameSuggestions;
    // দেনা পাওনা: no service list — the user always writes the name by hand.
    const nameOptions = denaPawna ? [] : (prefill?.name && !findSuggestionByName(baseSuggestions, prefill.name)
        ? [{ name: prefill.name, price: prefill.amount ?? null, category: prefill.category ?? null }, ...baseSuggestions]
        : baseSuggestions);

    // নগদ / বাকি segmented toggle — sits beside the preset amounts. Hidden in
    // দেনা পাওনা mode where entries are ALWAYS বাকি.
    const typeToggle = denaPawna ? null : (
        <div className="flex shrink-0 rounded-full border border-zinc-200 dark:border-zinc-700 p-0.5 bg-zinc-100 dark:bg-zinc-800" title={t('incomeExpense.markDueHint')}>
            <button
                type="button"
                onClick={() => setIsDue(false)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${!isDue ? 'bg-primary text-white shadow-sm' : 'text-zinc-500 dark:text-zinc-400'}`}
            >
                {t('incomeExpense.cash')}
            </button>
            <button
                type="button"
                onClick={() => setIsDue(true)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${isDue ? 'bg-primary text-white shadow-sm' : 'text-zinc-500 dark:text-zinc-400'}`}
            >
                {t('incomeExpense.due')}
            </button>
        </div>
    );

    // Floating "আজ" (today) badge amounts for the type toggle — due totals in
    // দেনা পাওনা mode, cash income/expense otherwise. Null until the fetch lands.
    // Thousands-grouped (Indian grouping: ২৩,৫৪২) so large day totals stay readable.
    const fmtMoney = (n) => {
        const grouped = Number(n || 0).toLocaleString('en-IN');
        return `৳${isBangla ? toBanglaDigits(grouped) : grouped}`;
    };
    const todayIncomeAmt  = todayTotals ? (denaPawna ? (todayTotals.totalDueIncome  || 0) : (todayTotals.totalIncome  || 0)) : null;
    const todayExpenseAmt = todayTotals ? (denaPawna ? (todayTotals.totalDueExpense || 0) : (todayTotals.totalExpense || 0)) : null;
    // A floating pill (white so it stays legible on both the active coloured button
    // and the inactive grey one), lifted above the button's top-right corner. Bigger
    // bold tabular digits so the amount is clear even at a glance.
    const todayBadge = (amount, tone) => (amount == null ? null : (
        <span className={`absolute -top-3.5 right-1 z-10 px-2.5 py-1 rounded-full text-[12.5px] font-extrabold tabular-nums leading-none shadow-md bg-white dark:bg-zinc-900 whitespace-nowrap pointer-events-none ring-1 ${tone === 'income' ? 'text-emerald-600 dark:text-emerald-400 ring-emerald-500/50' : 'text-red-600 dark:text-red-400 ring-red-500/50'}`}>
            {fmtMoney(amount)}
        </span>
    ));

    // ── Shared card (used by both the centered and floating layouts) ───────────
    const card = (
        <div
            className="w-[400px] max-w-[calc(100vw-20px)] bg-white dark:bg-zinc-900 rounded-[18px]"
            style={{ boxShadow: '0 20px 50px rgba(28,27,25,0.22)' }}
        >
            {/* ── Header ─────────────────────────────────────────── */}
            <div className={`flex items-center justify-between px-[18px] py-[15px] text-white rounded-t-[18px] ${denaPawna ? 'bg-sky-600' : 'bg-primary'}`}>
                <div className="flex items-center gap-2.5">
                    <div className="w-[30px] h-[30px] rounded-[9px] bg-white/20 flex items-center justify-center">
                        {isIncome ? <IconPlus size={17} /> : <IconMinus size={17} />}
                    </div>
                    <span className="text-[15px] font-bold">{isEditing
                        ? t(denaPawna ? 'incomeExpense.denaPawnaEditTitle' : 'incomeExpense.editEntryTitle')
                        : t(denaPawna ? 'incomeExpense.denaPawnaAddTitle' : 'incomeExpense.addEntryTitle')}</span>
                </div>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            onClick={closeModal}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                        >
                            <IconX size={15} />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="z-[300] font-mono">Esc</TooltipContent>
                </Tooltip>
            </div>

            <div className="px-[18px] pt-4 pb-[18px]">
                {/* ── Income / Expense toggle ────────────────────── */}
                <div className="flex gap-2 rounded-[11px] p-1 bg-zinc-100 dark:bg-zinc-800 mb-3.5">
                    <button
                        type="button"
                        onClick={() => switchType('income')}
                        className={`relative flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-[9px] text-[13.5px] font-semibold transition-all ${
                            isIncome
                                ? 'bg-emerald-500 text-white shadow-[0_2px_8px_rgba(47,165,110,0.32)]'
                                : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'
                        }`}
                    >
                        {todayBadge(todayIncomeAmt, 'income')}
                        <IconPlus size={15} /> {t(denaPawna ? 'incomeExpense.pawna' : 'incomeExpense.income')}
                    </button>
                    <button
                        type="button"
                        onClick={() => switchType('expense')}
                        className={`relative flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-[9px] text-[13.5px] font-semibold transition-all ${
                            !isIncome
                                ? 'bg-red-500 text-white shadow-[0_2px_8px_rgba(239,68,68,0.32)]'
                                : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'
                        }`}
                    >
                        {todayBadge(todayExpenseAmt, 'expense')}
                        <IconMinus size={15} /> {t(denaPawna ? 'incomeExpense.dena' : 'incomeExpense.expense')}
                    </button>
                </div>

                {/* ── Name — type manually, or pick from the active list's dropdown ─── */}
                <div className="mb-2.5" ref={nameBoxRef}>
                    <div className="relative">
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => { setName(e.target.value); if (e.target.value.trim()) setNameError(''); setNameOpen(true); }}
                            onFocus={() => setNameOpen(true)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.isComposing) { setNameOpen(false); handleSave(); } if (e.key === 'Escape') setNameOpen(false); }}
                            placeholder={denaPawna
                                ? (isIncome ? t('incomeExpense.pawnaNamePlaceholder') : t('incomeExpense.denaNamePlaceholder'))
                                : (isIncome ? t('incomeExpense.namePlaceholder') : t('incomeExpense.namePlaceholderExpense'))}
                            className={`w-full pl-3.5 pr-16 py-3 text-[13.5px] border rounded-[11px] bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 ${nameError ? 'border-red-400 focus:ring-red-400/40' : `border-zinc-200 dark:border-zinc-700 ${ringColor}`}`}
                        />
                        {name && (
                            <button
                                type="button"
                                onClick={() => { setName(''); setNameError(''); setNameOpen(false); }}
                                className={`absolute ${nameOptions.length > 0 ? 'right-9' : 'right-1.5'} top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-red-500`}
                                tabIndex={-1}
                            >
                                <IconX size={14} />
                            </button>
                        )}
                        {nameOptions.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setNameOpen((o) => !o)}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                                tabIndex={-1}
                            >
                                <IconChevronDown size={16} className={`transition-transform ${nameOpen ? 'rotate-180' : ''}`} />
                            </button>
                        )}
                        {nameOpen && (() => {
                            const q = norm(name).toLowerCase();
                            const list = nameOptions.filter((s) => !q || norm(s.name).toLowerCase().includes(q));
                            if (!list.length) return null;
                            return (
                                <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-[11px] shadow-lg max-h-52 overflow-y-auto py-1">
                                    {list.map((s) => {
                                        const active = norm(name) === norm(s.name);
                                        return (
                                            <button
                                                key={s.name}
                                                type="button"
                                                onClick={() => pickName(s)}
                                                className={`w-full flex items-center justify-between gap-2 text-left px-3.5 py-2 text-[13px] transition-colors ${active ? 'bg-primary/10 text-primary' : 'text-zinc-700 dark:text-zinc-200 hover:bg-primary/5 hover:text-primary'}`}
                                            >
                                                <span className="truncate">{s.name}</span>
                                                {s.price != null && (
                                                    <span className="flex-none font-semibold text-primary">৳{isBangla ? toBanglaDigits(String(s.price)) : s.price}</span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                    </div>
                    {nameError && <p className="text-[11px] font-medium text-red-500 mt-1">{nameError}</p>}
                </div>

                {/* ── Amount — big centered input with −10 / +10 steppers ── */}
                <div className="mb-3">
                    <div className="flex items-stretch gap-2">
                        <button
                            type="button"
                            onClick={() => stepAmount(-10)}
                            className="w-14 shrink-0 flex items-center justify-center rounded-[11px] border border-zinc-200 dark:border-zinc-700 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                            <IconMinus size={20} stroke={2.5} />
                        </button>
                        <div className={`flex-1 min-w-0 flex items-center gap-1 border rounded-[11px] px-3 h-[56px] bg-white dark:bg-zinc-800 focus-within:ring-2 ${amountError ? 'border-red-400 focus-within:ring-red-400/40' : `border-zinc-200 dark:border-zinc-700 ${ringColor}`}`}>
                            <IconCurrencyTaka size={20} className="text-zinc-400 shrink-0" />
                            <input
                                ref={amountRef}
                                type="text"
                                inputMode="decimal"
                                value={amountDisplay}
                                onChange={handleAmountChange}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.isComposing) handleSave(); }}
                                placeholder={isBangla ? '০' : '0'}
                                className="flex-1 min-w-0 bg-transparent border-none outline-none text-2xl font-bold text-center text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-300"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => stepAmount(10)}
                            className="w-14 shrink-0 flex items-center justify-center rounded-[11px] border border-zinc-200 dark:border-zinc-700 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                        >
                            <IconPlus size={20} stroke={2.5} />
                        </button>
                    </div>
                    {amountError && <p className="text-[11px] font-medium text-red-500 mt-1">{amountError}</p>}
                </div>

                {/* ── Quick amount chips + নগদ / বাকি ───────────────── */}
                <div className="flex items-center gap-2 mb-3">
                    <div className="flex-1 min-w-0 flex gap-1.5 flex-wrap">
                        {(denaPawna ? DENA_QUICK_AMOUNTS : QUICK_AMOUNTS).map(v => (
                            <button
                                key={v}
                                type="button"
                                onClick={() => setQuickAmount(v)}
                                className={`flex-1 min-w-[34px] py-1.5 rounded-full text-[13px] font-semibold border transition-colors ${
                                    parseFloat(toEnglishDigits(amountDisplay)) === v
                                        ? `${accentActive} border-transparent`
                                        : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-zinc-400'
                                }`}
                            >
                                {isBangla ? toBanglaDigits(String(v)) : v}
                            </button>
                        ))}
                    </div>
                    {typeToggle}
                </div>

                {/* ── Date (defaults to today; can be back-dated). When back-dated a
                       time picker appears beside it — today auto-uses the current
                       server time, an earlier day lets the user set the exact time. ── */}
                <div className="mb-3.5">
                    <label className="block text-[12px] font-medium text-zinc-500 dark:text-zinc-400 mb-1">
                        {t('incomeExpense.date')}
                    </label>
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1 min-w-0">
                            <DatePickerField
                                value={ymdToDate(date)}
                                onChange={(d) => setDate(dateToYmd(d) || localToday())}
                                placeholder={localToday()}
                                disabled={{ after: new Date() }}
                                triggerClassName={`w-full px-3.5 py-2.5 pr-10 text-[13.5px] border border-zinc-200 dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-800 focus:outline-none focus:ring-2 ${ringColor}`}
                            />
                            {date !== localToday() && (
                                <button
                                    type="button"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => { e.stopPropagation(); setDate(localToday()); }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-red-500"
                                    tabIndex={-1}
                                >
                                    <IconX size={14} />
                                </button>
                            )}
                        </div>
                        {date !== localToday() && (
                            <input
                                type="time"
                                value={time}
                                onChange={(e) => setTime(e.target.value || localNowTime())}
                                aria-label={t('incomeExpense.time')}
                                className={`shrink-0 w-[124px] px-3 py-2.5 text-[13.5px] border border-zinc-200 dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 ${ringColor}`}
                            />
                        )}
                    </div>
                </div>

                {/* ── Note ───────────────────────────────────────── */}
                <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.isComposing) handleSave(); }}
                    placeholder={t('incomeExpense.notePlaceholder')}
                    className={`w-full px-3.5 py-3 text-[13.5px] border border-zinc-200 dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 mb-3.5 ${ringColor}`}
                />

                {/* ── Customer (optional) — link a saved গ্রাহক; × clears it in one click ── */}
                <div className="mb-3.5">
                    <CustomerPicker
                        value={customerName}
                        linkedId={customerId}
                        onChange={(v) => { setCustomerName(v); setCustomerId(null); setCustomerPhone(''); setCustomerAddress(''); }}
                        onSelect={(m) => { setCustomerId(m.id); setCustomerName(m.name); setCustomerPhone(m.phone); setCustomerAddress(m.address || ''); }}
                        onClear={() => { setCustomerName(''); setCustomerId(null); setCustomerPhone(''); setCustomerAddress(''); }}
                        placeholder={t('incomeExpense.customerOptional')}
                        inputClassName={`w-full pl-3.5 pr-14 py-3 text-[13.5px] border rounded-[11px] bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 border-zinc-200 dark:border-zinc-700 ${ringColor}`}
                    />
                </div>

                {/* ── Save & Invoice (left) + Save (right) — 50/50. Save & Invoice is
                       hidden in edit mode (you're updating an existing entry). ─── */}
                <div className="flex gap-2">
                    {isIncome && !isEditing && (
                        <button
                            type="button"
                            onClick={handleSaveAndInvoice}
                            disabled={saving}
                            className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-[11px] text-sm font-bold border border-primary text-primary hover:bg-primary/5 disabled:opacity-50 transition-colors"
                        >
                            <IconFileInvoice size={16} /> {t('incomeExpense.saveAndInvoice')}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className={`flex-1 py-3 rounded-[11px] text-sm font-bold text-white transition-colors ${saveBg}`}
                    >
                        {saving ? t('incomeExpense.saving') : (isEditing ? (t('incomeExpense.saveChanges') || t('incomeExpense.save')) : t('incomeExpense.save'))}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div
            // Outside click deliberately does NOT close — half-filled entries were being
            // lost to stray clicks. Close via the red × button or Esc only.
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 animate-in fade-in p-4"
        >
            <div className="animate-in zoom-in-95">{card}</div>
        </div>
    );
}
